import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { PenTool, Settings, Phone, MessageSquare, ChevronRight, ChevronDown, Plus, Library, Languages } from "lucide-react";
import WorkflowEdit from "./WorkflowEdit.js";
import { useGroup } from "../context/GroupContext.js";
import { getOutboundCallUrl, getCurrentUserIdForCalls } from '../lib/apiConfig.js';
import { extractGreetingTemplateFromWorkflow, migrateWorkflowWithGreetingFromDoc } from "../lib/workflowGreeting.js";
import {
  AGENT_VOICE_OPTIONS as VOICE_OPTIONS,
  formatAgentVoiceLabel,
  LANGUAGE_CODE_LABELS,
} from "../constants/agentVoices.js";

const API_BASE = process.env.REACT_APP_AGENT_API_URL || '';
const SCRIPT_TYPES = ["Conversation Flow", "Text Prompt"];

async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {})
    },
    ...opts,
  });
  const raw = await res.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch { }
  if (!res.ok || data?.success === false) {
    const msg =
      (data && (data.error || data.message)) ||
      (raw && raw.trim()) ||
      `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
  return data ?? {};
}

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const TypeBadge = ({ type }) => (
  <span
    className={`text-[12px] font-semibold px-3 py-1 rounded-full ${type === "Conversation Flow"
        ? "bg-cyan-100 text-cyan-800 border border-cyan-300"
        : "bg-blue-100 text-blue-800 border border-blue-300"
      }`}
  >
    {type || "Text Prompt"}
  </span>
);

// Tone / Voice / Model options for script settings
const TONE_OPTIONS = [
  { value: "", label: "Default" },
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "empathetic", label: "Empathetic" },
  { value: "concise", label: "Concise" },
  { value: "warm", label: "Warm" },
];
const MODEL_OPTIONS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Default)" },
  { value: "gpt-4o", label: "GPT-4o (Advanced)" },
  { value: "claude-3-5-sonnet", label: "Claude 3.5 Sonnet" },
  { value: "claude-3-opus", label: "Claude 3 Opus" },
  { value: "llama-3-70b", label: "Llama 3 70B" },
  { value: "llama-2-70b", label: "Llama 2 70B" },
];
// Speech-to-text (Deepgram) + TTS engine options for voice-agent scripts.
// language=multi requires the nova-3 model; nova-2-phonecall is English-only, phone-optimized.
const STT_MODEL_OPTIONS = [
  { value: "", label: "Default (nova-2-phonecall)" },
  { value: "nova-2-phonecall", label: "Deepgram nova-2-phonecall (English, phone)" },
  { value: "nova-2", label: "Deepgram nova-2 (single-language, incl. Chinese)" },
  { value: "nova-3", label: "Deepgram nova-3 (single-language, incl. Chinese)" },
  { value: "gpt-transcribe", label: "OpenAI gpt-transcribe (auto EN+中文 in one call)" },
  { value: "gpt-4o-transcribe", label: "OpenAI gpt-4o-transcribe (auto EN+中文, higher accuracy)" },
];
const STT_LANGUAGE_OPTIONS = [
  { value: "", label: "Default (English)" },
  { value: "en", label: "English" },
  { value: "zh", label: "Chinese / 中文 (use nova-3)" },
  { value: "multi", label: "Multilingual code-switch — EN + European, NO Chinese" },
];
const TTS_PROVIDER_OPTIONS = [
  { value: "", label: "Auto (by voice)" },
  { value: "kokoro", label: "Kokoro (English)" },
  { value: "elevenlabs", label: "ElevenLabs (multilingual)" },
  { value: "cartesia", label: "Cartesia Sonic (multilingual)" },
];
const TTS_MODEL_OPTIONS = [
  { value: "", label: "Default (Flash v2.5 — fastest)" },
  { value: "eleven_flash_v2_5", label: "ElevenLabs Flash v2.5 (fast, multilingual)" },
  { value: "eleven_turbo_v2_5", label: "ElevenLabs Turbo v2.5 (balanced)" },
  { value: "eleven_multilingual_v2", label: "ElevenLabs Multilingual v2 (higher quality)" },
  { value: "eleven_v3", label: "ElevenLabs v3 (highest quality, slower)" },
];
const TONE_STYLE_OPTIONS = [
  "friendly and casual",
  "formal customer service",
  "warm and conversational",
  "gentle and reassuring",
  "confident sales agent",
  "neutral and informative",
  "nurturing nurse assistant",
  "laid-back and humorous",
  "instructional teacher",
];

// Translate target languages: same codes the voice stack supports (backend validates against TTS_LANGUAGES)
const TRANSLATE_LANGUAGES = Object.entries(LANGUAGE_CODE_LABELS).filter(([code]) => !code.includes("-"));

const SCRIPT_TAB_EDIT = "edit";
const SCRIPT_TAB_SETTINGS = "settings";
const SCRIPT_TAB_TRY = "try";

const newSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const DEFAULT_FROM_NUMBER = "(805) 500-0806";

const DEFAULT_EMPTY_WORKFLOW = JSON.stringify({ nodes: [], edges: [], agent: {} }, null, 2);

const ScriptManager = ({ createNewScript = false, onConsumeCreateNewScript, isOpinionService = false }) => {
  const { scopeOwnerIds } = useGroup() || {};
  const [scripts, setScripts] = useState([]);
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const [filteredScripts, setFilteredScripts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 200);
  const [typeFilter, setTypeFilter] = useState("All");

  const [isCreateMode, setIsCreateMode] = useState(false);
  const [selectedScriptId, setSelectedScriptId] = useState(null);
  const [selectedScriptName, setSelectedScriptName] = useState("");
  const [selectedType, setSelectedType] = useState("Text Prompt");
  const [editedScript, setEditedScript] = useState("");
  const [initialScriptText, setInitialScriptText] = useState("");

  const [scriptTab, setScriptTab] = useState(SCRIPT_TAB_EDIT);
  const [scriptSettings, setScriptSettings] = useState({
    scriptName: "",
    firstName: "",
    lastName: "",
    businessName: "",
    aiName: "",
    greetingTemplate: "",
    tone: "",
    voiceId: "",
    voiceName: "",
    model: "",
    toneStyle: "",
    responseDensity: "",
    empathyLevel: "",
    avoidTopics: "",
    formatting: "",
    guidelinesEnabled: false,
    validateAnswers: false,
    maxRetries: 2,
    sttModel: "",
    sttLanguage: "",
    ttsProvider: "",
    ttsModel: "",
  });
  const [initialScriptSettings, setInitialScriptSettings] = useState({
    scriptName: "",
    firstName: "",
    lastName: "",
    businessName: "",
    aiName: "",
    greetingTemplate: "",
    tone: "",
    voiceId: "",
    voiceName: "",
    model: "",
    toneStyle: "",
    responseDensity: "",
    empathyLevel: "",
    avoidTopics: "",
    formatting: "",
    guidelinesEnabled: false,
    validateAnswers: false,
    maxRetries: 2,
    sttModel: "",
    sttLanguage: "",
    ttsProvider: "",
    ttsModel: "",
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [translateLanguage, setTranslateLanguage] = useState("es");
  const [translating, setTranslating] = useState(false);

  const [chatSessionId, setChatSessionId] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [callDestination, setCallDestination] = useState("");
  const [callCalleeFirst, setCallCalleeFirst] = useState("");
  const [callCalleeLast, setCallCalleeLast] = useState("");
  const [ttsEngine, setTtsEngine] = useState("kokoro"); // TTS engine for TEN test calls: 'kokoro' | 'elevenlabs'
  const [elevenVoiceId, setElevenVoiceId] = useState("XrExE9yKIg1WjnnlVkGX"); // ElevenLabs voice for TEN calls (default Maya)
  const [testSttModel, setTestSttModel] = useState(""); // per-test-call STT model override (else script settings)
  const [testSttLanguage, setTestSttLanguage] = useState(""); // per-test-call STT language override
  const [testTtsModel, setTestTtsModel] = useState(""); // per-test-call ElevenLabs model override
  const [callStarting, setCallStarting] = useState(false);

  const [createDropdownOpen, setCreateDropdownOpen] = useState(false);
  const createDropdownRef = useRef(null);
  const [scriptIsGlobal, setScriptIsGlobal] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const hasUnsaved = useMemo(() => {
    const greetingDirty =
      selectedType !== "Conversation Flow" &&
      (scriptSettings.greetingTemplate || "") !== (initialScriptSettings.greetingTemplate || "");
    if (isCreateMode) return editedScript !== initialScriptText || !!scriptSettings.scriptName?.trim() || greetingDirty;
    return !!selectedScriptId && (editedScript !== initialScriptText || greetingDirty);
  }, [isCreateMode, selectedScriptId, editedScript, initialScriptText, scriptSettings.scriptName, scriptSettings.greetingTemplate, initialScriptSettings.greetingTemplate, selectedType]);

  const hasSettingsDirty = useMemo(() => {
    if (!selectedScriptId && !isCreateMode) return false;
    const a = scriptSettings;
    const b = initialScriptSettings;
    const greetingMismatch =
      selectedType !== "Conversation Flow" && a.greetingTemplate !== b.greetingTemplate;
    return (
      a.scriptName !== b.scriptName ||
      a.firstName !== b.firstName ||
      a.lastName !== b.lastName ||
      a.businessName !== b.businessName ||
      a.aiName !== b.aiName ||
      greetingMismatch ||
      a.tone !== b.tone ||
      a.voiceId !== b.voiceId ||
      a.voiceName !== b.voiceName ||
      a.model !== b.model ||
      a.toneStyle !== b.toneStyle ||
      a.responseDensity !== b.responseDensity ||
      a.empathyLevel !== b.empathyLevel ||
      a.avoidTopics !== b.avoidTopics ||
      a.formatting !== b.formatting ||
      !!a.guidelinesEnabled !== !!b.guidelinesEnabled ||
      !!a.validateAnswers !== !!b.validateAnswers ||
      a.maxRetries !== b.maxRetries ||
      a.sttModel !== b.sttModel ||
      a.sttLanguage !== b.sttLanguage ||
      a.ttsProvider !== b.ttsProvider ||
      a.ttsModel !== b.ttsModel
    );
  }, [selectedScriptId, scriptSettings, initialScriptSettings, selectedType]);

  const greetingTemplateForTry = useMemo(() => {
    if (selectedType !== "Conversation Flow") return scriptSettings.greetingTemplate || null;
    try {
      const w = JSON.parse(editedScript || "{}");
      const g = extractGreetingTemplateFromWorkflow(w);
      return g || null;
    } catch {
      return scriptSettings.greetingTemplate || null;
    }
  }, [selectedType, editedScript, scriptSettings.greetingTemplate]);

  const scriptDataForTry = useMemo(() => {
    const id = selectedScriptId || (isCreateMode ? "draft" : null);
    if (!id) return null;
    return {
      _id: id,
      scriptName: scriptSettings.scriptName || selectedScriptName || "New script",
      script: editedScript,
      type: selectedType,
      firstName: scriptSettings.firstName || null,
      lastName: scriptSettings.lastName || null,
      businessName: scriptSettings.businessName || null,
      aiName: scriptSettings.aiName || null,
      greetingTemplate: greetingTemplateForTry,
      settings: {
        tone: scriptSettings.tone,
        voiceId: scriptSettings.voiceId,
        voiceName: scriptSettings.voiceName,
        model: scriptSettings.model,
        guidelinesEnabled: !!scriptSettings.guidelinesEnabled,
        sttModel: scriptSettings.sttModel,
        sttLanguage: scriptSettings.sttLanguage,
        ttsProvider: scriptSettings.ttsProvider,
        ttsModel: scriptSettings.ttsModel,
      },
    };
  }, [selectedScriptId, isCreateMode, selectedScriptName, selectedType, editedScript, scriptSettings, greetingTemplateForTry]);

  const workflowExternalAgent = useMemo(() => {
    if (selectedType !== "Conversation Flow") return null;
    return {
      voice: scriptSettings.voiceId || undefined,
      toneStyle: scriptSettings.toneStyle || undefined,
      responseDensity: scriptSettings.responseDensity || undefined,
      empathyLevel: scriptSettings.empathyLevel || undefined,
      avoidTopics: scriptSettings.avoidTopics || undefined,
      formatting: scriptSettings.formatting || undefined,
    };
  }, [selectedType, scriptSettings]);

  const workflowFlushRef = useRef(null);

  const workflowInitialData = useMemo(() => {
    try {
      const parsed = JSON.parse(editedScript || "{}");
      if (!parsed.agent) parsed.agent = {};
      return parsed;
    } catch (err) {
      console.error("Error parsing script JSON:", err);
      return { nodes: [], edges: [], agent: {} };
    }
  }, [editedScript]);

  /** Only changes when a script is loaded or saved — prevents canvas reset on tab/settings re-render */
  const workflowLoadKey = useMemo(
    () => `${selectedScriptId ?? "new"}:${initialScriptText}`,
    [selectedScriptId, initialScriptText]
  );

  const handleWorkflowChange = useCallback((scriptStr) => {
    setEditedScript(scriptStr);
  }, []);

  const registerWorkflowFlush = useCallback((fn) => {
    workflowFlushRef.current = fn;
  }, []);

  const switchScriptTab = (tab) => {
    if (
      scriptTab === SCRIPT_TAB_EDIT &&
      tab !== SCRIPT_TAB_EDIT &&
      selectedType === "Conversation Flow"
    ) {
      workflowFlushRef.current?.();
    }
    setScriptTab(tab);
  };

  useEffect(() => {
    fetchScripts();
  }, [scopeOwnerIds]);

  const enterCreateMode = (type = "Text Prompt", initialScriptTextOrWorkflow = null) => {
    if (hasUnsaved || hasSettingsDirty) {
      const ok = window.confirm("You have unsaved changes. Discard them and create a new script?");
      if (!ok) return;
    }
    setCreateDropdownOpen(false);
    setSelectedScriptId(null);
    setSelectedScriptName(initialScriptTextOrWorkflow && type === "Text Prompt" ? "Example: Voice Assistant" : "New script");
    setSelectedType(type);
    if (type === "Conversation Flow") {
      setEditedScript(DEFAULT_EMPTY_WORKFLOW);
      setInitialScriptText(DEFAULT_EMPTY_WORKFLOW);
    } else {
      const text = initialScriptTextOrWorkflow != null ? String(initialScriptTextOrWorkflow) : "";
      setEditedScript(text);
      setInitialScriptText(text);
    }
    setScriptTab(SCRIPT_TAB_EDIT);
    const isExample = initialScriptTextOrWorkflow && type === "Text Prompt";
    const emptySettings = {
      scriptName: isExample ? "Example: Voice Assistant" : "",
      firstName: "", lastName: "", businessName: "", aiName: "",
      greetingTemplate: "",
      tone: "", voiceId: "", voiceName: "", model: "",
      toneStyle: "", responseDensity: "", empathyLevel: "", avoidTopics: "", formatting: "",
      guidelinesEnabled: false,
    };
    setScriptSettings(emptySettings);
    setInitialScriptSettings(emptySettings);
    setChatSessionId("");
    setChatMessages([]);
    setChatInput("");
    setCallDestination("");
    setCallCalleeFirst("");
    setCallCalleeLast("");
    setError("");
    setIsCreateMode(true);
  };

  useEffect(() => {
    if (createNewScript) {
      enterCreateMode("Text Prompt");
      if (typeof onConsumeCreateNewScript === 'function') onConsumeCreateNewScript();
    }
  }, [createNewScript]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(e.target)) {
        setCreateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const term = debouncedSearch.trim().toLowerCase();
    setFilteredScripts(
      scripts.filter((s) => {
        const name = (s.scriptName || "").toLowerCase();
        const id = (s._id || "").toLowerCase();
        const t = s.type || "Text Prompt";
        const okName = term ? name.includes(term) || id.includes(term) : true;
        const okType = typeFilter === "All" ? true : t === typeFilter;
        return okName && okType;
      })
    );
  }, [debouncedSearch, typeFilter, scripts]);

  const fetchScripts = async () => {
    setError("");
    setLoadingList(true);
    try {
      const data = await apiFetch("/api/scripts");
      let list = (data.scripts || [])
        .map((s) => ({ ...s, type: s.type || "Text Prompt" }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (currentUser && Number(currentUser.userlevel) === 100) {
        const myId = currentUser.id || currentUser._id || currentUser.userId;
        const allowedIds = scopeOwnerIds && scopeOwnerIds.length > 0 ? scopeOwnerIds : [myId];
        // Show own/group scripts + global scripts (ownerId "global" = visible to everyone; backend also filters)
        list = list.filter(s => s.ownerId === 'global' || allowedIds.some(id => String(s.ownerId) === String(id)));
      }
      // Filter based on opinion service flag
      if (isOpinionService) {
        list = list.filter(s => s.ownerId === 'opinion-service-global');
      } else {
        list = list.filter(s => s.ownerId !== 'opinion-service-global');
      }
      setScripts(list);
      setFilteredScripts(list);
    } catch (err) {
      setError(`Error fetching scripts: ${err.message}`);
    } finally {
      setLoadingList(false);
    }
  };

  const loadScript = async (_id, scriptName) => {
    if (hasUnsaved) {
      const ok = window.confirm("You have unsaved changes. Discard them?");
      if (!ok) return;
    }
    setError("");
    setLoadingItem(true);
    setSelectedScriptId(_id);
    setSelectedScriptName(scriptName);
    setEditedScript("");
    setInitialScriptText("");
    setSelectedType("Text Prompt");
    setScriptTab(SCRIPT_TAB_EDIT);

    try {
      const data = await apiFetch(`/api/script/${_id}`);
      const doc = data.script || {};
      let scriptBody = doc.script || "";
      if ((doc.type || "").toString() === "Conversation Flow" && doc.script) {
        try {
          const parsed = JSON.parse(doc.script);
          const migrated = migrateWorkflowWithGreetingFromDoc(parsed, doc.greetingTemplate);
          scriptBody = JSON.stringify(migrated, null, 2);
        } catch (_) {
          /* keep raw */
        }
      }
      setEditedScript(scriptBody);
      setInitialScriptText(scriptBody);
      setSelectedType(doc.type || "Text Prompt");
      setSelectedScriptName(doc.scriptName || scriptName);
      const s = doc.settings && typeof doc.settings === "object" ? doc.settings : {};
      let agent = {};
      if ((doc.type || "").toString() === "Conversation Flow" && scriptBody) {
        try {
          const parsed = JSON.parse(scriptBody);
          agent = parsed.agent && typeof parsed.agent === "object" ? parsed.agent : {};
        } catch (_) {}
      }
      const nextSettings = {
        scriptName: String(doc.scriptName || scriptName || "").trim(),
        firstName: String(doc.firstName || "").trim(),
        lastName: String(doc.lastName || "").trim(),
        businessName: String(doc.businessName || "").trim(),
        aiName: String(doc.aiName || "").trim(),
        greetingTemplate: String(doc.greetingTemplate || "").trim(),
        tone: String(s.tone || agent.toneStyle || "").trim(),
        voiceId: String(s.voiceId || agent.voice || "").trim(),
        voiceName: String(s.voiceName || "").trim(),
        model: String(s.model || "").trim(),
        toneStyle: String(s.toneStyle || agent.toneStyle || "").trim(),
        responseDensity: String(s.responseDensity || agent.responseDensity || "").trim(),
        empathyLevel: String(s.empathyLevel || agent.empathyLevel || "").trim(),
        avoidTopics: String(s.avoidTopics || agent.avoidTopics || "").trim(),
        formatting: String(s.formatting || agent.formatting || "").trim(),
        guidelinesEnabled: !!(s.guidelinesEnabled),
        sttModel: String(s.sttModel || "").trim(),
        sttLanguage: String(s.sttLanguage || "").trim(),
        ttsProvider: String(s.ttsProvider || "").trim(),
        ttsModel: String(s.ttsModel || "").trim(),
      };
      setScriptSettings(nextSettings);
      setInitialScriptSettings(nextSettings);
      setScriptIsGlobal(doc.ownerId === 'global');
    } catch (err) {
      setError(`Error loading script: ${err.message}`);
    } finally {
      setLoadingItem(false);
    }
  };

  const saveScript = async (overrideScript) => {
    if (selectedType === "Conversation Flow" && overrideScript == null) {
      workflowFlushRef.current?.();
    }
    if (isCreateMode) {
      const name = (scriptSettings.scriptName || selectedScriptName || "New script").trim() || "New script";
      const scriptBody = overrideScript != null ? String(overrideScript) : String(editedScript ?? "");
      setSaving(true);
      setError("");
      try {
        const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
        let greetingForCreate = scriptSettings.greetingTemplate;
        if (selectedType === "Conversation Flow") {
          try {
            greetingForCreate = extractGreetingTemplateFromWorkflow(JSON.parse(scriptBody || "{}"));
          } catch (_) {
            greetingForCreate = "";
          }
        }
        const data = await apiFetch("/api/save-script", {
          method: "POST",
          body: JSON.stringify({
            scriptName: name,
            script: scriptBody,
            type: selectedType,
            firstName: scriptSettings.firstName || undefined,
            lastName: scriptSettings.lastName || undefined,
            businessName: scriptSettings.businessName || undefined,
            aiName: scriptSettings.aiName || undefined,
            greetingTemplate: greetingForCreate || undefined,
            settings: {
              tone: scriptSettings.tone || undefined,
              voiceId: scriptSettings.voiceId || undefined,
              voiceName: scriptSettings.voiceName || undefined,
              model: scriptSettings.model || undefined,
              toneStyle: scriptSettings.toneStyle || undefined,
              responseDensity: scriptSettings.responseDensity || undefined,
              empathyLevel: scriptSettings.empathyLevel || undefined,
              avoidTopics: scriptSettings.avoidTopics || undefined,
              formatting: scriptSettings.formatting || undefined,
              guidelinesEnabled: !!scriptSettings.guidelinesEnabled,
              validateAnswers: !!scriptSettings.validateAnswers,
              maxRetries: Number(scriptSettings.maxRetries) || 2,
              sttModel: scriptSettings.sttModel || undefined,
              sttLanguage: scriptSettings.sttLanguage || undefined,
              ttsProvider: scriptSettings.ttsProvider || undefined,
              ttsModel: scriptSettings.ttsModel || undefined,
            },
            ...(isOpinionService ? { ownerId: 'opinion-service-global' } : {}),
          }),
        });
        const newId = data.id || data._id;
        if (newId) {
          const savedScript = scriptBody || editedScript;
          setIsCreateMode(false);
          setSelectedScriptId(newId);
          setSelectedScriptName(name);
          setEditedScript(savedScript);
          setInitialScriptText(savedScript);
          setInitialScriptSettings({ ...scriptSettings });
          setScripts((prev) => [{ _id: newId, scriptName: name, type: selectedType, script: savedScript, createdAt: new Date() }, ...prev]);
          alert("✅ Script created.");
        }
      } catch (err) {
        setError(`Error creating script: ${err.message}`);
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!selectedScriptId) return;
    setSaving(true);
    setError("");

    const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
    let greetingForUpdate = scriptSettings.greetingTemplate;
    if (selectedType === "Conversation Flow") {
      try {
        greetingForUpdate = extractGreetingTemplateFromWorkflow(JSON.parse(String(editedScript ?? "{}")));
      } catch (_) {
        greetingForUpdate = "";
      }
    }
    const payload = {
      _id: String(selectedScriptId),
      script: String(editedScript ?? ""),
      scriptName: scriptSettings.scriptName || selectedScriptName,
      greetingTemplate: greetingForUpdate || "",
      ...(ownerId ? { ownerId } : {}),
    };

    try {
      await apiFetch("/api/update-script", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setInitialScriptText(payload.script);
      setInitialScriptSettings((prev) => ({ ...prev, greetingTemplate: payload.greetingTemplate || "" }));
      if (payload.scriptName) setSelectedScriptName(payload.scriptName);
      setScripts((prev) =>
        prev.map((s) =>
          s._id === selectedScriptId ? { ...s, ...payload } : s
        )
      );

      alert("✅ Script updated.");
    } catch (err) {
      setError(`Error saving script: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const translateScript = async () => {
    if (!selectedScriptId) return;
    if (hasUnsaved && !window.confirm("Unsaved changes won't be translated. Continue with the last saved version?")) return;
    setTranslating(true);
    setError("");
    try {
      const data = await apiFetch("/api/translate-script", {
        method: "POST",
        body: JSON.stringify({ _id: selectedScriptId, language: translateLanguage }),
      });
      await fetchScripts();
      // Only jump to the new script when nothing is dirty — loadScript would otherwise re-prompt.
      if (data.id && !hasUnsaved) await loadScript(data.id, data.scriptName);
      alert(`✅ Created "${data.scriptName}".`);
    } catch (err) {
      setError(`Error translating script: ${err.message}`);
    } finally {
      setTranslating(false);
    }
  };

  const deleteScript = async () => {
    if (!selectedScriptId) return;
    const ok = window.confirm(`Delete script "${selectedScriptName}"?`);
    if (!ok) return;
    setDeleting(true);
    setError("");
    try {
      await apiFetch("/api/delete-script", {
        method: "POST",
        body: JSON.stringify({ _id: selectedScriptId }),
      });
      alert("🗑️ Script deleted.");
      goBack(true);
      await fetchScripts();
    } catch (err) {
      setError(`Error deleting script: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const saveSettings = async () => {
    if (!selectedScriptId) return;
    if (selectedType === "Conversation Flow") {
      workflowFlushRef.current?.();
    }
    setSavingSettings(true);
    setError("");
    const settings = {
      tone: scriptSettings.tone,
      voiceId: scriptSettings.voiceId,
      voiceName: scriptSettings.voiceName,
      model: scriptSettings.model,
      toneStyle: scriptSettings.toneStyle,
      responseDensity: scriptSettings.responseDensity,
      empathyLevel: scriptSettings.empathyLevel,
      avoidTopics: scriptSettings.avoidTopics,
      formatting: scriptSettings.formatting,
      guidelinesEnabled: !!scriptSettings.guidelinesEnabled,
      sttModel: scriptSettings.sttModel || undefined,
      sttLanguage: scriptSettings.sttLanguage || undefined,
      ttsProvider: scriptSettings.ttsProvider || undefined,
      ttsModel: scriptSettings.ttsModel || undefined,
    };
    let scriptPayload = undefined;
    if (selectedType === "Conversation Flow" && editedScript) {
      try {
        const workflow = JSON.parse(editedScript);
        workflow.agent = {
          ...(workflow.agent || {}),
          voice: scriptSettings.voiceId || undefined,
          toneStyle: scriptSettings.toneStyle || undefined,
          responseDensity: scriptSettings.responseDensity || undefined,
          empathyLevel: scriptSettings.empathyLevel || undefined,
          avoidTopics: scriptSettings.avoidTopics || undefined,
          formatting: scriptSettings.formatting || undefined,
        };
        scriptPayload = JSON.stringify(workflow, null, 2);
      } catch (_) {}
    }
    const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;
    let greetingForSettings = scriptSettings.greetingTemplate;
    if (selectedType === "Conversation Flow" && scriptPayload) {
      try {
        greetingForSettings = extractGreetingTemplateFromWorkflow(JSON.parse(scriptPayload));
      } catch (_) {
        greetingForSettings = "";
      }
    }
    try {
      await apiFetch("/api/update-script", {
        method: "POST",
        body: JSON.stringify({
          _id: selectedScriptId,
          scriptName: scriptSettings.scriptName || undefined,
          firstName: scriptSettings.firstName || undefined,
          lastName: scriptSettings.lastName || undefined,
          businessName: scriptSettings.businessName || undefined,
          aiName: scriptSettings.aiName || undefined,
          greetingTemplate: greetingForSettings || undefined,
          settings,
          ...(scriptPayload != null ? { script: scriptPayload } : {}),
          ...(ownerId ? { ownerId } : {}),
          isGlobal: scriptIsGlobal,
        }),
      });
      setInitialScriptSettings({ ...scriptSettings });
      if (scriptSettings.scriptName) setSelectedScriptName(scriptSettings.scriptName);
      if (scriptPayload != null) {
        setEditedScript(scriptPayload);
        setInitialScriptText(scriptPayload);
      }
      setScripts((prev) =>
        prev.map((s) =>
          s._id === selectedScriptId
            ? { ...s, scriptName: scriptSettings.scriptName || s.scriptName, ownerId: scriptIsGlobal ? 'global' : (ownerId || s.ownerId) }
            : s
        )
      );
      alert("✅ Settings saved.");
    } catch (err) {
      setError(`Error saving settings: ${err.message}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const goBack = (force = false) => {
    if (!force && (hasUnsaved || hasSettingsDirty)) {
      const ok = window.confirm(
        "You have unsaved changes. Discard them and go back?"
      );
      if (!ok) return;
    }
    setIsCreateMode(false);
    setSelectedScriptId(null);
    setSelectedScriptName("");
    setScriptIsGlobal(false);
    setEditedScript("");
    setInitialScriptText("");
    setSelectedType("Text Prompt");
    setScriptTab(SCRIPT_TAB_EDIT);
    const emptySettings = {
      scriptName: "", firstName: "", lastName: "", businessName: "", aiName: "",
      greetingTemplate: "",
      tone: "", voiceId: "", voiceName: "", model: "",
      toneStyle: "", responseDensity: "", empathyLevel: "", avoidTopics: "", formatting: "",
      guidelinesEnabled: false,
    };
    setScriptSettings(emptySettings);
    setInitialScriptSettings(emptySettings);
    setChatSessionId("");
    setChatMessages([]);
    setChatInput("");
    setCallDestination("");
    setCallCalleeFirst("");
    setCallCalleeLast("");
    setError("");
  };

  const formatDate = (isoDate) => {
    const d = new Date(isoDate);
    return isNaN(d) ? "" : d.toLocaleString();
  };

  const startChatSession = () => {
    if (!scriptDataForTry) return;
    const sid = newSessionId();
    setChatSessionId(sid);
    const businessName = scriptDataForTry.businessName || "our office";
    const aiName = scriptDataForTry.aiName || "Maya";
    setChatMessages([
      { role: "assistant", content: `Hi there! This is ${aiName} from ${businessName}. How can I help today?` },
    ]);
    setChatInput("");
  };

  const sendChatMessage = async () => {
    const userMsg = chatInput.trim();
    if (!userMsg || chatSending || !scriptDataForTry) return;
    setChatInput("");
    const newHistory = [...chatMessages, { role: "user", content: userMsg }];
    setChatMessages(newHistory);
    setChatSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/chatbot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("token") ? { Authorization: `Bearer ${localStorage.getItem("token")}` } : {}),
        },
        body: JSON.stringify({
          sessionId: chatSessionId,
          scriptData: scriptDataForTry,
          fileId: null,
          query: userMsg,
          messages: newHistory,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setChatMessages((m) => [...m, { role: "assistant", content: `Error: ${data?.message || res.status}` }]);
      } else {
        setChatMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      }
    } catch (err) {
      setChatMessages((m) => [...m, { role: "assistant", content: `Network error: ${err.message}` }]);
    } finally {
      setChatSending(false);
    }
  };

  const startCall = async (useTen = false) => {
    const to = callDestination.trim().replace(/\D/g, "");
    if (!to) {
      alert("Enter a destination phone number.");
      return;
    }
    if (!scriptDataForTry) return;
    setCallStarting(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const placedByUserId = getCurrentUserIdForCalls();
      const sd = {
        ...scriptDataForTry,
        firstName: callCalleeFirst.trim() || scriptDataForTry.firstName || null,
        lastName: callCalleeLast.trim() || scriptDataForTry.lastName || null,
      };
      const body = {
        to: callDestination.trim(),
        from: DEFAULT_FROM_NUMBER,
        stack: "kokoro-vad",
        preferBodyScript: true,
        ...(placedByUserId ? { placedByUserId } : {}),
        scriptData: sd,
        fileId: null,
        // When ElevenLabs is chosen, use the ElevenLabs voice picked in the test panel;
        // otherwise fall back to the script's configured voice.
        voiceId: ttsEngine === "elevenlabs"
          ? elevenVoiceId
          : (scriptDataForTry.settings?.voiceId || VOICE_OPTIONS[0].id),
        // TTS engine for the TEN path — user's per-call choice from the sidebar.
        ttsProvider: ttsEngine,
        // Per-test-call STT overrides (blank → server falls back to the script's saved settings).
        ...(ttsEngine === "elevenlabs" && testTtsModel ? { ttsModel: testTtsModel } : {}),
        ...(testSttModel ? { sttModel: testSttModel } : {}),
        ...(testSttLanguage ? { sttLanguage: testSttLanguage } : {}),
        // Route audio through the TEN framework server (V2 points Twilio at TEN when useTen is set).
        ...(useTen ? { useTen: true } : {}),
        // Omit firstMessage so the server uses script "FIRST MESSAGE" line (supports {{firstName}}, etc.) or default intro
      };
      // Both buttons hit the V2 outbound endpoint; `useTen` in the body decides who handles the audio.
      const outboundUrl = getOutboundCallUrl();
      console.log(`[OutboundCall${useTen ? " TEN" : ""}] POST`, outboundUrl, "| hasToken:", !!token, "| scriptId:", body?.scriptData?._id);
      const res = await fetch(outboundUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      console.log("[OutboundCall] Response", res.status, "| body length:", raw?.length ?? 0);
      if (!res.ok) console.warn("[OutboundCall] Body:", raw?.substring(0, 400));
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        console.error("[OutboundCall] Not JSON:", raw?.substring(0, 500));
        setError('Server returned an invalid response.');
        return;
      }
      if (res.ok && data.success) {
        alert(`Call started (${useTen ? "TEN" : "legacy"}). Call SID: ${data.callSid}`);
        setCallDestination("");
        setCallCalleeFirst("");
        setCallCalleeLast("");
      } else {
        setError(data?.message || "Failed to start call.");
      }
    } catch (err) {
      setError(`Error starting call: ${err.message}`);
    } finally {
      setCallStarting(false);
    }
  };

  const inEditor = !!selectedScriptId || isCreateMode;

  return (
    <div className="w-full h-full min-h-0 flex flex-col bg-[#f0f0f0]">
      {/* LIST MODE */}
      {!inEditor && (
        <div className="w-full flex-1 min-h-0 flex flex-col bg-[#f0f0f0] p-6 overflow-hidden">
          <div className="flex items-center justify-between mb-6 gap-4">
            <h1 className="text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
              All Agents
            </h1>
            <div className="relative shrink-0" ref={createDropdownRef}>
                <button
                  type="button"
                  onClick={() => setCreateDropdownOpen((v) => !v)}
                  className="bg-[#459EA3] hover:bg-[#27AAE1] text-white px-4 py-2 rounded-md font-bold uppercase inline-flex items-center gap-1.5"
                  style={{ fontFamily: "'Canva Sans', sans-serif" }}
                >
                  Create New Agent
                  <ChevronDown className={`w-4 h-4 transition-transform ${createDropdownOpen ? "rotate-180" : ""}`} />
                </button>
              {createDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 py-1 min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {SCRIPT_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => enterCreateMode(t)}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-[#459EA3]/10 hover:text-[#459EA3] flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4 text-gray-400" />
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <select
              className="border px-2 py-2 rounded"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option>All</option>
              {SCRIPT_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Search by name or ID..."
              className="flex-1 border px-3 py-2 rounded"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {error && (
            <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 p-2 rounded">
              {error}
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-auto space-y-3">
            {filteredScripts.length === 0 && !loadingList && (
              <p className="text-gray-500">No scripts found.</p>
            )}
            {filteredScripts.map((s) => (
              <div
                key={s._id}
                className="group w-full border border-gray-200 p-3 rounded-lg bg-white hover:border-gray-300 transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      loadScript(s._id, s.scriptName);
                    }}
                    className="flex items-center justify-center w-10 h-10 bg-[#27AAE1] hover:bg-[#459EA3] text-white rounded-lg transition-colors flex-shrink-0"
                    title="Edit Script"
                  >
                    <PenTool className="w-5 h-5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate text-base group-hover:text-blue-600 transition-colors">
                        {s.scriptName}
                      </h3>
                      <TypeBadge type={s.type} />
                      {s.ownerId === 'global' && (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300" title="Visible to everyone">
                          Template
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {formatDate(s.createdAt)}
                      </span>
                      <span className="text-xs text-gray-400">
                        · Owner: {s.ownerId === 'global'
                          ? "Template"
                          : (String(s.ownerId) === String(currentUser?.id || currentUser?._id || currentUser?.userId) ? "You" : "—")}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 font-mono truncate max-w-[120px] text-right flex-shrink-0">
                    {s._id}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* EDITOR MODE: main area + pullout (when open) + right icon rail - all in flex so rail is always visible */}
      {inEditor && (
        <div className="flex w-full flex-1 min-h-0 bg-[#f0f0f0] overflow-hidden flex-col lg:flex-row">
          {/* Main content: script */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden shrink-0">
            <header className="flex items-center gap-3 px-4 py-2 bg-gray-100 rounded-b-xl shrink-0 flex-wrap">
              <button
                onClick={() => goBack()}
                className="text-gray-500 hover:text-gray-800 text-sm py-1.5 pr-2 -ml-1 rounded hover:bg-gray-200/80 transition-colors"
              >
                ← Back
              </button>
              {isCreateMode && (
                <span className="text-xs font-medium text-gray-500 px-2 py-1 rounded bg-gray-100">
                  {selectedType}
                </span>
              )}
              <span className="text-sm font-medium text-gray-700 truncate flex-1">
                {isCreateMode ? "New script" : selectedScriptName}
              </span>
              {(hasUnsaved || (hasSettingsDirty && scriptTab !== SCRIPT_TAB_EDIT)) && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-medium shrink-0">
                  Unsaved
                </span>
              )}
              {!isCreateMode && selectedScriptId && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <select
                    value={translateLanguage}
                    onChange={(e) => setTranslateLanguage(e.target.value)}
                    disabled={translating}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    aria-label="Translation language"
                  >
                    {TRANSLATE_LANGUAGES.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                  <button
                    onClick={translateScript}
                    disabled={translating}
                    title="Save a translated copy as a new script"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#459EA3] bg-[#459EA3]/10 hover:bg-[#459EA3]/20 disabled:opacity-60 transition-colors"
                  >
                    <Languages size={15} />
                    {translating ? "Translating…" : "Translate"}
                  </button>
                </div>
              )}
            </header>

            {error && (
              <div className="mx-6 mt-4 text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
                {error}
              </div>
            )}

            {loadingItem ? (
              <div className="flex-1 flex items-center justify-center text-gray-500">Loading script…</div>
            ) : (
              <main className="flex-1 min-h-0 overflow-auto p-6 flex flex-col">
                {/* Script content (always visible in main area) */}
                {selectedType === "Conversation Flow" ? (
                  <div className="flex flex-col flex-1 min-h-0 gap-4">
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-[#F9F9F9]">
                      <WorkflowEdit
                        initialData={workflowInitialData}
                        workflowLoadKey={workflowLoadKey}
                        scriptId={isCreateMode ? null : selectedScriptId}
                        hideAgentSettings
                        externalAgent={workflowExternalAgent}
                        onWorkflowChange={handleWorkflowChange}
                        onRegisterFlush={registerWorkflowFlush}
                        onSaveDraft={isCreateMode ? async (workflowJson) => {
                          const scriptStr = JSON.stringify(workflowJson, null, 2);
                          setEditedScript(scriptStr);
                          setInitialScriptText(scriptStr);
                          await saveScript(scriptStr);
                        } : undefined}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col min-h-0 flex-1 min-h-[320px] bg-transparent gap-4">
                    <div className="shrink-0 rounded-xl border border-[#459EA3]/20 bg-white p-4 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-800 mb-1">Opening greeting (voice calls)</label>
                      <p className="text-xs text-gray-500 mb-2">
                        Text prompt: optional first line on phone calls. Leave blank for the default intro. Placeholders:{" "}
                        <code className="text-[11px] bg-gray-100 px-1 rounded">{"{{aiName}}"}</code>,{" "}
                        <code className="text-[11px] bg-gray-100 px-1 rounded">{"{{firstName}}"}</code>,{" "}
                        <code className="text-[11px] bg-gray-100 px-1 rounded">{"{{businessName}}"}</code>, etc.
                      </p>
                      <textarea
                        rows={3}
                        value={scriptSettings.greetingTemplate}
                        onChange={(e) => setScriptSettings((prev) => ({ ...prev, greetingTemplate: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                        placeholder='Hi, this is {{aiName}} from {{businessName}}. Am I speaking with {{firstName}}?'
                      />
                    </div>
                    <div className="flex flex-col flex-1 min-h-0 rounded-xl border border-gray-200/80 bg-white overflow-hidden">
                      <div className="text-xs font-semibold text-gray-500 px-3 pt-3 shrink-0">Main prompt</div>
                    <textarea
                      className="flex-1 min-h-[240px] w-full border-0 p-4 font-mono text-sm text-gray-800 placeholder-gray-400 focus:ring-0 focus:outline-none resize-none bg-transparent"
                      value={editedScript}
                      onChange={(e) => setEditedScript(e.target.value)}
                      placeholder="Enter your main instructions for the AI (not the opening phone greeting — use the field above)."
                    />
                    </div>
                    <div className="px-4 py-3 border-t border-gray-200/50 flex gap-3 items-center bg-transparent">
                      <button
                        onClick={saveScript}
                        disabled={saving || (!hasUnsaved && !isCreateMode)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${hasUnsaved || isCreateMode
                          ? "bg-[#459EA3] hover:bg-[#3d8a8e]"
                          : "bg-gray-300 cursor-not-allowed"
                          }`}
                      >
                        {saving ? "Saving…" : isCreateMode ? "Create script" : "Save"}
                      </button>
                      {!isCreateMode && (
                        <button
                          onClick={deleteScript}
                          disabled={deleting}
                          className="bg-red-500/10 hover:bg-red-500/20 text-red-600 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60 transition-colors"
                        >
                          {deleting ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </main>
            )}

          </div>

          {/* Pullout panel (Settings or Try) - flex child; toggle on left edge like sidebar */}
          {(scriptTab === SCRIPT_TAB_SETTINGS || scriptTab === SCRIPT_TAB_TRY) && (
            <div className="relative w-full lg:w-[420px] lg:max-w-[45vw] max-h-[55vh] lg:max-h-none shrink-0 flex flex-col bg-gray-100 rounded-l-2xl overflow-visible border-t lg:border-t-0 lg:border-l border-gray-200/50">
              <button
                type="button"
                onClick={() => switchScriptTab(SCRIPT_TAB_EDIT)}
                className="hidden lg:flex absolute left-[-14px] top-[24px] z-10 w-7 h-7 rounded-full bg-gray-200 shadow-sm items-center justify-center text-gray-500 hover:bg-[#DBF4F3] hover:text-[#459EA3] transition-all"
                title="Slide in"
                aria-label="Slide in"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200/50 shrink-0">
                <span className="font-semibold text-gray-900">
                  {scriptTab === SCRIPT_TAB_SETTINGS ? "Agent settings" : "Try it"}
                </span>
                {/* Small screens: the left-edge chevron is off-canvas, so give an in-panel way to
                    collapse back to the editor (otherwise the panel hides the whole editor). */}
                <button
                  type="button"
                  onClick={() => switchScriptTab(SCRIPT_TAB_EDIT)}
                  className="lg:hidden inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-[#459EA3] px-2 py-1 -mr-2 rounded"
                  title="Back to editor"
                  aria-label="Back to editor"
                >
                  <ChevronRight className="w-4 h-4" />
                  Editor
                </button>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0">
                  {scriptTab === SCRIPT_TAB_SETTINGS && (
                    <div className="p-4 space-y-6">
                      {currentUser && Number(currentUser.userlevel) >= 200 && !isCreateMode && (
                        <div className="pb-4 border-b border-gray-200">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!scriptIsGlobal}
                              onChange={(e) => setScriptIsGlobal(e.target.checked)}
                              className="rounded border-gray-300 text-[#459EA3] focus:ring-[#459EA3]"
                            />
                            <span className="text-sm font-semibold text-gray-700">Visible to everyone (global)</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-1 ml-6">When on, this script appears for all users. Only admins can change this.</p>
                        </div>
                      )}
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Agent identity</div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Script name</label>
                    <input
                      type="text"
                      value={scriptSettings.scriptName}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, scriptName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      placeholder="Agent name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">First name (contact)</label>
                      <input
                        type="text"
                        value={scriptSettings.firstName}
                        onChange={(e) => setScriptSettings((prev) => ({ ...prev, firstName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Last name (contact)</label>
                      <input
                        type="text"
                        value={scriptSettings.lastName}
                        onChange={(e) => setScriptSettings((prev) => ({ ...prev, lastName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Business name</label>
                    <input
                      type="text"
                      value={scriptSettings.businessName}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, businessName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      placeholder="e.g. Our office"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">AI / Agent name</label>
                    <input
                      type="text"
                      value={scriptSettings.aiName}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, aiName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      placeholder="e.g. Maya"
                    />
                  </div>
                  {selectedType !== "Conversation Flow" && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Opening greeting (voice calls)</label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Same field as in Build. Optional — blank uses default intro. Use{" "}
                      <code className="text-[11px] bg-gray-100 px-1 rounded">{"{{aiName}}"}</code>,{" "}
                      <code className="text-[11px] bg-gray-100 px-1 rounded">{"{{firstName}}"}</code>, etc.
                    </p>
                    <textarea
                      rows={3}
                      value={scriptSettings.greetingTemplate}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, greetingTemplate: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                      placeholder='Hi, this is {{aiName}} from {{businessName}}. Am I speaking with {{firstName}}?'
                    />
                  </div>
                  )}
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-4 border-t border-gray-200/50">Tone / Voice / Model</div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tone</label>
                    <select
                      value={scriptSettings.tone}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, tone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    >
                      {TONE_OPTIONS.map((o) => (
                        <option key={o.value || "default"} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Voice</label>
                    <select
                      value={scriptSettings.voiceId}
                      onChange={(e) => {
                        const opt = VOICE_OPTIONS.find((v) => v.id === e.target.value);
                        setScriptSettings((prev) => ({
                          ...prev,
                          voiceId: e.target.value,
                          voiceName: opt ? opt.name : "",
                        }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    >
                      <option value="">Default</option>
                      {VOICE_OPTIONS.map((v) => (
                        <option key={v.id} value={v.id}>
                          {formatAgentVoiceLabel(v)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Model</label>
                    <select
                      value={scriptSettings.model}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, model: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    >
                      {MODEL_OPTIONS.map((o) => (
                        <option key={o.value || "default"} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Speech recognition (STT)</label>
                    <select
                      value={scriptSettings.sttModel}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, sttModel: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    >
                      {STT_MODEL_OPTIONS.map((o) => (
                        <option key={o.value || "default"} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">STT language</label>
                    <select
                      value={scriptSettings.sttLanguage}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, sttLanguage: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    >
                      {STT_LANGUAGE_OPTIONS.map((o) => (
                        <option key={o.value || "default"} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Text-to-speech (TTS)</label>
                    <select
                      value={scriptSettings.ttsProvider}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, ttsProvider: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    >
                      {TTS_PROVIDER_OPTIONS.map((o) => (
                        <option key={o.value || "default"} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">ElevenLabs model</label>
                    <select
                      value={scriptSettings.ttsModel}
                      onChange={(e) => setScriptSettings((prev) => ({ ...prev, ttsModel: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                    >
                      {TTS_MODEL_OPTIONS.map((o) => (
                        <option key={o.value || "default"} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pt-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Turn on / off guidelines</label>
                    <button
                      type="button"
                      onClick={() =>
                        setScriptSettings((prev) => ({
                          ...prev,
                          guidelinesEnabled: !prev.guidelinesEnabled,
                        }))
                      }
                      className={`w-full inline-flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                        scriptSettings.guidelinesEnabled
                          ? "border-[#459EA3] bg-[#DBF4F3] text-[#2d7a7e]"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                      aria-pressed={scriptSettings.guidelinesEnabled}
                    >
                      <span>{scriptSettings.guidelinesEnabled ? "Guidelines ON" : "Guidelines OFF"}</span>
                      <span
                        className={`inline-block h-2.5 w-2.5 rounded-full ${
                          scriptSettings.guidelinesEnabled ? "bg-emerald-500" : "bg-gray-400"
                        }`}
                      />
                    </button>
                    <p className="mt-1 text-xs text-gray-500">
                      When ON, strict anti-hallucination RAG guidelines are injected into the backend system prompt at session start.
                    </p>
                  </div>
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-4 border-t border-gray-200/50">Workflow agent (Conversation Flow)</div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Tone style</label>
                        <select
                          value={scriptSettings.toneStyle}
                          onChange={(e) => setScriptSettings((prev) => ({ ...prev, toneStyle: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                        >
                          <option value="">Select tone style</option>
                          {TONE_STYLE_OPTIONS.map((style) => (
                            <option key={style} value={style}>{style}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Response density</label>
                        <select
                          value={scriptSettings.responseDensity}
                          onChange={(e) => setScriptSettings((prev) => ({ ...prev, responseDensity: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                        >
                          <option value="">Select density</option>
                          <option value="brief">Brief</option>
                          <option value="balanced">Balanced</option>
                          <option value="detailed">Detailed</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Empathy level</label>
                        <select
                          value={scriptSettings.empathyLevel}
                          onChange={(e) => setScriptSettings((prev) => ({ ...prev, empathyLevel: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                        >
                          <option value="">Select level</option>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Avoid topics</label>
                        <textarea
                          value={scriptSettings.avoidTopics}
                          onChange={(e) => setScriptSettings((prev) => ({ ...prev, avoidTopics: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                          rows={2}
                          placeholder="Comma-separated list"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Formatting preferences</label>
                        <textarea
                          value={scriptSettings.formatting}
                          onChange={(e) => setScriptSettings((prev) => ({ ...prev, formatting: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                          rows={2}
                          placeholder="Comma-separated list"
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={scriptSettings.validateAnswers}
                            onChange={(e) => setScriptSettings((prev) => ({ ...prev, validateAnswers: e.target.checked }))}
                            className="w-4 h-4 accent-[#459EA3]"
                          />
                          <span className="text-sm font-semibold text-gray-700">Validate answers (if answer doesn't match question, ask again)</span>
                        </label>
                        {scriptSettings.validateAnswers && (
                          <div className="mt-2 ml-6">
                            <label className="block text-sm text-gray-700 mb-1">Max retries</label>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={scriptSettings.maxRetries}
                              onChange={(e) => setScriptSettings((prev) => ({ ...prev, maxRetries: Number(e.target.value) || 2 }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                            />
                          </div>
                        )}
                      </div>

                      <div className="pt-2">
                        <button
                          onClick={saveSettings}
                          disabled={savingSettings || !hasSettingsDirty}
                          className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${hasSettingsDirty
                            ? "bg-[#459EA3] hover:bg-[#3d8a8e]"
                            : "bg-gray-300 cursor-not-allowed"
                            }`}
                        >
                          {savingSettings ? "Saving…" : "Save settings"}
                        </button>
                      </div>
                    </div>
                  )}
                  {scriptTab === SCRIPT_TAB_TRY && (
                    <div className="flex flex-col gap-4 p-4">
                      {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-2 rounded-lg">{error}</div>
                      )}
                      <div className="grid grid-cols-1 gap-4">
                    {/* Inline chat */}
                    <div className="border border-gray-200/50 rounded-lg bg-transparent flex flex-col overflow-hidden">
                      <div className="bg-gradient-to-r from-[#459EA3] to-[#27AAE1] text-white px-4 py-2 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        <span className="font-semibold">Chat</span>
                      </div>
                      <div className="flex-1 min-h-[200px] max-h-[320px] overflow-y-auto p-3 space-y-2 bg-gray-50">
                        {chatMessages.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-6">
                            {chatSessionId ? "Send a message below." : "Click Start session to begin."}
                          </p>
                        )}
                        {chatMessages.map((m, i) => (
                          <div
                            key={i}
                            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                                m.role === "user"
                                  ? "bg-[#459EA3] text-white"
                                  : "bg-white border border-gray-200 text-gray-800"
                              }`}
                            >
                              {m.content}
                            </div>
                          </div>
                        ))}
                        {chatSending && (
                          <div className="flex justify-start">
                            <div className="bg-white/80 border border-gray-200/60 rounded-lg px-3 py-2 text-sm text-gray-500">
                              Thinking…
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-3 border-t border-gray-200/50 flex gap-2">
                        {!chatSessionId ? (
                          <button
                            type="button"
                            onClick={startChatSession}
                            disabled={!scriptDataForTry}
                            className="flex-1 py-2 rounded-lg bg-[#459EA3] hover:bg-[#27AAE1] text-white font-medium disabled:opacity-50"
                          >
                            Start session
                          </button>
                        ) : (
                          <>
                            <input
                              type="text"
                              value={chatInput}
                              onChange={(e) => setChatInput(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendChatMessage()}
                              placeholder="Type a message…"
                              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                              disabled={chatSending}
                            />
                            <button
                              type="button"
                              onClick={sendChatMessage}
                              disabled={!chatInput.trim() || chatSending}
                              className="px-4 py-2 rounded-lg bg-[#459EA3] text-white font-medium disabled:opacity-50"
                            >
                              Send
                            </button>
                            <button
                              type="button"
                              onClick={() => { setChatSessionId(""); setChatMessages([]); setChatInput(""); }}
                              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm"
                            >
                              Reset
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Inline call start */}
                    <div className="border border-gray-200/50 rounded-lg bg-transparent flex flex-col overflow-hidden">
                      <div className="bg-gradient-to-r from-[#27AAE1] to-[#459EA3] text-white px-4 py-2 flex items-center gap-2">
                        <Phone className="w-5 h-5" />
                        <span className="font-semibold">Voice call</span>
                      </div>
                      <div className="p-4 space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Destination number</label>
                          <input
                            type="tel"
                            value={callDestination}
                            onChange={(e) => setCallDestination(e.target.value)}
                            placeholder="+15555551234"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Callee first name</label>
                            <input
                              type="text"
                              value={callCalleeFirst}
                              onChange={(e) => setCallCalleeFirst(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Callee last name</label>
                            <input
                              type="text"
                              value={callCalleeLast}
                              onChange={(e) => setCallCalleeLast(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Voice engine (TEN)</label>
                          <select
                            value={ttsEngine}
                            onChange={(e) => setTtsEngine(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                          >
                            <option value="kokoro">Kokoro (fast, default)</option>
                            <option value="elevenlabs">ElevenLabs (higher quality)</option>
                          </select>
                          <p className="text-xs text-gray-400 mt-1">Applies to "Start call (TEN)".</p>
                        </div>
                        {ttsEngine === "elevenlabs" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">ElevenLabs voice</label>
                            <select
                              value={elevenVoiceId}
                              onChange={(e) => setElevenVoiceId(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                            >
                              {VOICE_OPTIONS.filter((v) => v.provider === "ElevenLabs").map((v) => (
                                <option key={v.id} value={v.id}>{formatAgentVoiceLabel(v)}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-400 mt-1">Used for this call when ElevenLabs is selected.</p>
                            <label className="block text-sm font-medium text-gray-600 mb-1 mt-3">ElevenLabs model</label>
                            <select
                              value={testTtsModel}
                              onChange={(e) => setTestTtsModel(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                            >
                              {TTS_MODEL_OPTIONS.map((o) => (
                                <option key={o.value || "default"} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Speech recognition</label>
                            <select
                              value={testSttModel}
                              onChange={(e) => setTestSttModel(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                            >
                              {STT_MODEL_OPTIONS.map((o) => (
                                <option key={o.value || "default"} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">STT language</label>
                            <select
                              value={testSttLanguage}
                              onChange={(e) => setTestSttLanguage(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#459EA3]"
                            >
                              {STT_LANGUAGE_OPTIONS.map((o) => (
                                <option key={o.value || "default"} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 -mt-1">Overrides the script's saved STT for this test call. For live EN/中文 in one call pick OpenAI gpt-transcribe; for Chinese-only pick nova-3 + Chinese.</p>
                        {/* Single call button — routes through the TEN framework server (audio in
                            TEN, workflow in V2). The legacy non-TEN button was removed to avoid
                            confusion; startCall(false) is still available if we need it back. */}
                        <button
                          type="button"
                          onClick={() => startCall(true)}
                          disabled={!scriptDataForTry || callStarting}
                          title="Place the test call through the TEN framework server (audio in TEN, workflow in V2)"
                          className="w-full py-3 rounded-lg bg-[#27AAE1] hover:bg-[#459EA3] text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          <Phone className="w-4 h-4" />
                          {callStarting ? "Starting…" : "Start call"}
                        </button>
                        <p className="text-xs text-gray-500">
                          From: {DEFAULT_FROM_NUMBER}
                        </p>
                      </div>
                    </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Right rail: Step 1, Step 2, Step 3 - fill full height */}
          <div className="w-28 shrink-0 flex flex-col items-stretch min-h-0 py-2 gap-2 bg-[#e5e7eb] border-l border-gray-300/80 rounded-l-xl shadow-sm">
            <button
              type="button"
              onClick={() => switchScriptTab(SCRIPT_TAB_EDIT)}
              title="Step 1: Build Agent"
              className={`flex-1 flex flex-col items-center justify-center gap-2 p-2 rounded-xl transition-all min-h-0 ${scriptTab === SCRIPT_TAB_EDIT
                ? "bg-[#459EA3] text-white shadow-md"
                : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                }`}
            >
              <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">Step 1</span>
              <PenTool className="w-6 h-6 shrink-0" />
              <span className="text-xs font-semibold leading-tight text-center">Build Agent</span>
            </button>
            <button
              type="button"
              onClick={() => switchScriptTab(SCRIPT_TAB_SETTINGS)}
              title="Step 2: Agent Setting"
              className={`flex-1 flex flex-col items-center justify-center gap-2 p-2 rounded-xl transition-all min-h-0 ${scriptTab === SCRIPT_TAB_SETTINGS
                ? "bg-[#459EA3] text-white shadow-md"
                : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                }`}
            >
              <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">Step 2</span>
              <Settings className="w-6 h-6 shrink-0" />
              <span className="text-xs font-semibold leading-tight text-center">Agent Setting</span>
            </button>
            <button
              type="button"
              onClick={() => switchScriptTab(SCRIPT_TAB_TRY)}
              title="Step 3: Test Agent"
              className={`flex-1 flex flex-col items-center justify-center gap-2 p-2 rounded-xl transition-all min-h-0 ${scriptTab === SCRIPT_TAB_TRY
                ? "bg-[#459EA3] text-white shadow-md"
                : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                }`}
            >
              <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">Step 3</span>
              <Phone className="w-6 h-6 shrink-0" />
              <span className="text-xs font-semibold leading-tight text-center">Test Agent</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScriptManager;
